import { Component, ViewEncapsulation, OnInit, Injectable, ChangeDetectorRef, ElementRef, ViewChild, computed, signal, Input, Output, EventEmitter, AfterViewInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormControl, FormGroup, Validators, FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule, MAT_DATE_LOCALE, DateAdapter, NativeDateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import { MatIconModule } from '@angular/material/icon';
import { DatePipe } from '@angular/common';
import { NgSelectModule } from '@ng-select/ng-select';
import { ProjectCompletionModalData } from '@app/shared/services/dialog.service';
import { Spinner } from '../spinner/spinner';

// Custom date formats
export const MY_DATE_FORMATS = {
  parse: {
    dateInput: 'MMM D, YYYY',
  },
  display: {
    dateInput: 'MMM D, YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

// Custom Date Adapter to format display
@Injectable()
export class CustomDateAdapter extends NativeDateAdapter {
  override format(date: Date, displayFormat: Object): string {
    if (displayFormat === 'dateInput') {
      const month = date.toLocaleDateString('en-US', { month: 'short' }).toUpperCase();
      const day = date.getDate();
      const year = date.getFullYear();
      return `${month} ${day}, ${year}`;
    }
    // For other display formats, use the parent implementation
    return super.format(date, displayFormat);
  }

  override parse(value: any): Date | null {
    // Handle the formatted date string (e.g., "DEC 22, 2025" or "Dec 22, 2025")
    if (typeof value === 'string' && value.includes(',')) {
      const parts = value.split(',');
      const monthDay = parts[0].trim();
      const year = parseInt(parts[1].trim());

      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                         'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const monthDayParts = monthDay.split(' ');
      const monthName = monthDayParts[0];
      // Handle both uppercase and capitalized month names
      const month = monthNames.findIndex(m => 
        m.toLowerCase() === monthName.toLowerCase()
      );
      const day = parseInt(monthDayParts[1]);

      if (month !== -1 && !isNaN(day) && !isNaN(year)) {
        return new Date(year, month, day);
      }
    }

    // Handle MM/DD/YYYY format
    if (typeof value === 'string' && value.indexOf('/') > -1) {
      const str = value.split('/');
      const year = Number(str[2]);
      const month = Number(str[0]) - 1;
      const date = Number(str[1]);
      return new Date(year, month, date);
    }

    const timestamp = typeof value === 'number' ? value : Date.parse(value);
    return isNaN(timestamp) ? null : new Date(timestamp);
  }
}

@Component({
  selector: 'app-project-completion-modal',
  templateUrl: './project-completion-modal.html',
  styleUrls: ['./project-completion-modal.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatInputModule,
    MatFormFieldModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatIconModule,
    FormsModule,
    NgSelectModule,
    Spinner
  ],
  providers: [
    DatePipe,
    { provide: MAT_DATE_LOCALE, useValue: 'en-US' },
    { provide: DateAdapter, useClass: CustomDateAdapter },
    { provide: MAT_DATE_FORMATS, useValue: MY_DATE_FORMATS }
  ],
  encapsulation: ViewEncapsulation.None
})
export class ProjectCompletionModal implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('dateInput', { static: false }) dateInput!: ElementRef<HTMLInputElement>;
  @Input() modalData: ProjectCompletionModalData = {};
  @Output() modalClosed = new EventEmitter<void>();
  @Output() formSubmitted = new EventEmitter<any>();

  completionForm: FormGroup;
  minDate = new Date();
  maxDate = new Date(new Date().setFullYear(new Date().getFullYear() + 5));

  showSlider = signal(false);
  displayDateValue = signal('');
  isLoading = signal(false);
  
  private documentClickListener?: (event: Event) => void;
  private formatCheckInterval?: any;

  currentProjectedDate = computed(() => this.modalData?.currentProjectedDate);
  currentResponsibility = computed(() => this.modalData?.currentResponsibility);
  projectId = computed(() => this.modalData?.projectId);
  responsibilities = signal<Array<{ name: string; id?: string }>>([
    { name: 'TW-INTL', id: 'tw-intl' },
    { name: 'TW-CAN', id: 'tw-can' },
    { name: 'WL-Errors', id: 'wl-errors' },
    { name: 'Client', id: 'client' },
  ]);

  constructor(
    private datePipe: DatePipe,
    private dateAdapter: DateAdapter<Date>,
    private cdr: ChangeDetectorRef
  ) {
    this.completionForm = new FormGroup({
      responsibleTeam: new FormControl(null, [Validators.required]),
      completionDate: new FormControl('', [Validators.required]),
      notes: new FormControl('', [Validators.required])
    });
  }

    ngOnInit(): void {
    // Set the date adapter format
    this.dateAdapter.setLocale('en-US');

    // Initialize form with current data
    this.initializeFormWithData();

    // Set up reactive form listeners
    this.setupFormListeners();

    // Show the slider
    this.showSlider.set(true);
  }

  ngAfterViewInit(): void {
    // Update input field value after view is initialized
    if (this.modalData?.currentProjectedDate) {
      this.updateInputFieldValue();
    }
    
    // Set up MutationObserver and document click listener to maintain date format
    this.setupDateFormatMaintenance();
  }
  
  ngOnDestroy(): void {
    // Clean up listeners
    if (this.documentClickListener) {
      document.removeEventListener('click', this.documentClickListener, true);
      const focusInHandler = (this.documentClickListener as any).focusInHandler;
      if (focusInHandler) {
        document.removeEventListener('focusin', focusInHandler, true);
      }
    }
    if (this.formatCheckInterval) {
      clearInterval(this.formatCheckInterval);
    }
  }
  
  /**
   * Sets up mechanisms to maintain the custom date format
   */
  private setupDateFormatMaintenance(): void {
    // Wait for the input element to be available
    setTimeout(() => {
      if (this.dateInput?.nativeElement) {
        // Store the original value property descriptor
        const inputElement = this.dateInput.nativeElement;
        let lastCorrectValue = '';
        
        // Override the value property to intercept changes
        const self = this;
        Object.defineProperty(inputElement, 'value', {
          get: function() {
            return this.getAttribute('value') || '';
          },
          set: function(newValue: string) {
            const dateValue = self.completionForm.get('completionDate')?.value;
            if (dateValue && dateValue instanceof Date && !isNaN(dateValue.getTime())) {
              const expectedFormat = self.dateAdapter.format(dateValue, 'dateInput');
              // If the new value is in wrong format (contains slashes like MM/DD/YYYY), replace it
              if (newValue && typeof newValue === 'string' && newValue.includes('/')) {
                newValue = expectedFormat;
                lastCorrectValue = expectedFormat;
              } else if (newValue && typeof newValue === 'string' && 
                        !newValue.match(/^[A-Z]{3}\s+\d{1,2},\s+\d{4}$/)) {
                // If it doesn't match our format pattern (e.g., "DEC 22, 2025"), fix it
                // But only if it's not empty
                if (newValue.trim() !== '') {
                  newValue = expectedFormat;
                  lastCorrectValue = expectedFormat;
                }
              } else {
                lastCorrectValue = newValue || expectedFormat;
              }
            }
            // Set the value using the native setter
            Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value')?.set?.call(this, newValue);
          },
          configurable: true
        });
      }
    }, 100);
    
    // Listen to document clicks to fix format when clicking elsewhere
    this.documentClickListener = () => {
      // Use multiple timeouts to catch Angular Material's formatting attempts
      setTimeout(() => this.ensureCorrectDateFormat(), 10);
      setTimeout(() => this.ensureCorrectDateFormat(), 50);
      setTimeout(() => this.ensureCorrectDateFormat(), 100);
      setTimeout(() => this.ensureCorrectDateFormat(), 200);
    };
    document.addEventListener('click', this.documentClickListener, true);
    
    // Also listen to focus events on other elements
    const focusInHandler = (event: Event) => {
      const target = event.target as HTMLElement;
      // If focus moved to a different element (not the date input), fix the date format
      if (target !== this.dateInput?.nativeElement && 
          !this.dateInput?.nativeElement?.contains(target)) {
        setTimeout(() => this.ensureCorrectDateFormat(), 10);
        setTimeout(() => this.ensureCorrectDateFormat(), 50);
        setTimeout(() => this.ensureCorrectDateFormat(), 100);
      }
    };
    document.addEventListener('focusin', focusInHandler, true);
    // Store the handler for cleanup
    (this.documentClickListener as any).focusInHandler = focusInHandler;
    
    // Periodic check to ensure format is maintained (as a fallback)
    this.formatCheckInterval = setInterval(() => {
      this.ensureCorrectDateFormat();
    }, 200);
  }
  
  /**
   * Ensures the date input displays the correct format
   */
  private ensureCorrectDateFormat(): void {
    const dateValue = this.completionForm.get('completionDate')?.value;
    if (dateValue && dateValue instanceof Date && !isNaN(dateValue.getTime())) {
      const expectedFormat = this.dateAdapter.format(dateValue, 'dateInput');
      this.displayDateValue.set(expectedFormat);
      
      if (this.dateInput?.nativeElement) {
        const currentValue = this.dateInput.nativeElement.value;
        // Check if the format is incorrect (contains slashes or doesn't match expected format)
        if (currentValue && (currentValue.includes('/') || currentValue !== expectedFormat)) {
          // Force update the value
          this.dateInput.nativeElement.setAttribute('value', expectedFormat);
          this.dateInput.nativeElement.value = expectedFormat;
          // Trigger input event to ensure Angular Material recognizes the change
          this.dateInput.nativeElement.dispatchEvent(new Event('input', { bubbles: true }));
          this.cdr.detectChanges();
        }
      }
    }
  }

    private initializeFormWithData(): void {

      // Update responsibilities first if available in data
    if (this.modalData?.responsibilities && this.modalData.responsibilities.length > 0) {
      this.responsibilities.set(this.modalData.responsibilities);
    }
    // Set current projected date if available
    if (this.modalData?.currentProjectedDate) {
      const date = new Date(this.modalData.currentProjectedDate);
      this.completionForm.patchValue({
        completionDate: date
      });
      this.displayDateValue.set(this.dateAdapter.format(date, 'dateInput'));
      this.updateInputFieldValue();
    }

    // Set current responsibility if available
    if (this.modalData?.currentResponsibility) {
      const currentResp = this.modalData.currentResponsibility;
      // Try to find by exact match first
      let responsibility = this.responsibilities().find(r =>
        r.name === currentResp || r.id === currentResp
      );
      
      // If not found, try to find by normalized name (remove spaces and dashes)
      if (!responsibility) {
        const normalizedCurrent = currentResp.replace(/[\s-]/g, '').toLowerCase();
        responsibility = this.responsibilities().find(r => {
          const normalizedName = r.name.replace(/[\s-]/g, '').toLowerCase();
          const normalizedId = r.id?.replace(/[\s-]/g, '').toLowerCase();
          return normalizedName === normalizedCurrent || normalizedId === normalizedCurrent;
        });
      }

      if (responsibility) {
        this.completionForm.patchValue({
          responsibleTeam: responsibility.id || responsibility.name
        });
      }
    }

   
  }

  private setupFormListeners(): void {
    // Listen for date changes and update display value
    const completionDateControl = this.completionForm.get('completionDate');
    if (completionDateControl) {
      completionDateControl.valueChanges.subscribe(value => {
        if (value && value instanceof Date && !isNaN(value.getTime())) {
          this.displayDateValue.set(this.dateAdapter.format(value, 'dateInput'));
          this.updateInputFieldValue();
        } else {
          this.displayDateValue.set('');
        }
        this.cdr.detectChanges();
      });
    }
  }

  // Handle date picker closed event
  onDatePickerClosed(): void {
    const dateValue = this.completionForm.get('completionDate')?.value;
    if (dateValue && dateValue instanceof Date && !isNaN(dateValue.getTime())) {
      this.displayDateValue.set(this.dateAdapter.format(dateValue, 'dateInput'));
      this.updateInputFieldValue();
    } else {
      this.displayDateValue.set('');
    }
    this.cdr.detectChanges();
  }

  // Handle date selection
  onDateSelected(event: any): void {
    if (event.value instanceof Date) {
      this.displayDateValue.set(this.dateAdapter.format(event.value, 'dateInput'));
      this.updateInputFieldValue();
      this.cdr.detectChanges();
    }
  }

  onCancel(): void {
    // Reset loading state if user cancels during save
    if (this.isLoading()) {
      this.isLoading.set(false);
    }
    this.closeSlider();
  }

  onClose(): void {
    // Reset loading state if user closes during save
    if (this.isLoading()) {
      this.isLoading.set(false);
    }
    this.closeSlider();
  }

  private closeSlider(): void {
    this.showSlider.set(false);
    this.modalClosed.emit();
  }

  onSave(): void {
    // Mark all form controls as touched to trigger validation
    this.markFormGroupTouched(this.completionForm);

    if (this.completionForm.valid && !this.isLoading()) {
      this.isLoading.set(true);
      const formValue = this.completionForm.value;
      const result = {
        ...formValue,
        projectId: this.projectId(),
        completionDate: this.datePipe.transform(formValue.completionDate, 'MMM d, yyyy'),
        originalDate: this.currentProjectedDate(),
        originalResponsibility: this.currentResponsibility()
      };

      this.formSubmitted.emit(result);
      // Don't close immediately - let parent component handle closing after save completes
      // The parent should call closeModal() or handle the modal closing
    }
  }

  // Method to close modal from parent component after save completes
  closeModal(): void {
    this.isLoading.set(false);
    this.closeSlider();
  }

  /**
   * Marks all controls in a form group as touched
   */
  private markFormGroupTouched(formGroup: FormGroup) {
    Object.values(formGroup.controls).forEach(control => {
      if (control instanceof FormControl) {
        control.markAsTouched({ onlySelf: true });
      } else if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

  private formatDate(date: Date): string {
    return this.datePipe.transform(date, 'MMM d, yyyy') || '';
  }

  /**
   * Updates the input field value to show the formatted date
   */
  private updateInputFieldValue(): void {
    // Use requestAnimationFrame to ensure DOM is ready
    requestAnimationFrame(() => {
      if (this.dateInput?.nativeElement) {
        const formattedValue = this.displayDateValue();
        if (formattedValue) {
          this.dateInput.nativeElement.value = formattedValue;
        }
      }
    });
  }

  /**
   * Ensures custom date format is applied when the date input loses focus
   */
  onDateInputBlur(): void {
    const dateValue = this.completionForm.get('completionDate')?.value;
    if (dateValue && dateValue instanceof Date && !isNaN(dateValue.getTime())) {
      const formattedDate = this.dateAdapter.format(dateValue, 'dateInput');
      this.displayDateValue.set(formattedDate);
      // Use multiple timeouts to ensure Angular Material has finished processing
      setTimeout(() => this.ensureCorrectDateFormat(), 0);
      setTimeout(() => this.ensureCorrectDateFormat(), 10);
      setTimeout(() => this.ensureCorrectDateFormat(), 50);
      setTimeout(() => this.ensureCorrectDateFormat(), 100);
      this.cdr.detectChanges();
    }
  }

  /**
   * Ensures custom date format is maintained when the date input gains focus
   */
  onDateInputFocus(): void {
    const dateValue = this.completionForm.get('completionDate')?.value;
    if (dateValue && dateValue instanceof Date && !isNaN(dateValue.getTime())) {
      this.displayDateValue.set(this.dateAdapter.format(dateValue, 'dateInput'));
      this.updateInputFieldValue();
      this.cdr.detectChanges();
    }
  }

  /**
   * Opens the date picker when the input field is clicked
   */
  onDateInputClick(): void {
    // The picker.open() will be called directly from the template
  }
}
